import { API_CONFIG } from '../../config/api';

// API Error Response Interface
export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, any>;
    requestId?: string;
  };
}

// Rate Limit Headers Interface
export interface RateLimit {
  limit: number;
  remaining: number;
  reset: number;
}

// Custom API Error Class
export class ApiClientError extends Error {
  public readonly status: number;
  public readonly errorCode?: string;
  public readonly details?: Record<string, any>;
  public readonly requestId?: string;
  public readonly rateLimit?: Partial<RateLimit>;
  public readonly retryAfter?: number;

  constructor(
    message: string,
    status: number,
    errorCode?: string,
    details?: Record<string, any>,
    requestId?: string,
    rateLimit?: Partial<RateLimit>,
    retryAfter?: number
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.errorCode = errorCode;
    this.details = details;
    this.requestId = requestId;
    this.rateLimit = rateLimit;
    this.retryAfter = retryAfter;
  }
}

// Enhanced fetch with authentication, retries, and error handling
class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    // Extract rate limit headers
    const rateLimit: Partial<RateLimit> = {
      limit: response.headers.get('X-RateLimit-Limit') ? 
        parseInt(response.headers.get('X-RateLimit-Limit')!) : undefined,
      remaining: response.headers.get('X-RateLimit-Remaining') ? 
        parseInt(response.headers.get('X-RateLimit-Remaining')!) : undefined,
      reset: response.headers.get('X-RateLimit-Reset') ? 
        parseInt(response.headers.get('X-RateLimit-Reset')!) : undefined,
    };

    // Handle rate limiting (429)
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      const delay = retryAfter ? parseInt(retryAfter) * 1000 : API_CONFIG.RETRY_DELAY;
      
      throw new ApiClientError(
        `Rate limit exceeded. Retry after ${delay}ms`,
        429,
        'rate_limit_exceeded',
        undefined,
        undefined,
        rateLimit,
        delay
      );
    }

    // Handle authentication errors (401)
    if (response.status === 401) {
      this.token = null; // Clear invalid token
      throw new ApiClientError(
        'Authentication failed',
        401,
        'unauthorized',
        undefined,
        undefined,
        rateLimit
      );
    }

    const text = await response.text();
    
    if (!response.ok) {
      try {
        const errorData = JSON.parse(text) as ApiError;
        throw new ApiClientError(
          errorData.error.message,
          response.status,
          errorData.error.code,
          errorData.error.details,
          errorData.error.requestId,
          rateLimit
        );
      } catch (parseError) {
        throw new ApiClientError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          'http_error',
          undefined,
          undefined,
          rateLimit
        );
      }
    }

    if (!text) {
      return {} as T;
    }

    try {
      return JSON.parse(text) as T;
    } catch (parseError) {
      throw new ApiClientError(
        'Invalid JSON response',
        response.status,
        'json_parse_error',
        undefined,
        undefined,
        rateLimit
      );
    }
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async fetchWithRetry<T>(
    url: string, 
    options: RequestInit = {},
    retryCount = 0
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

    try {
      const response = await fetch(url, {
        ...options,
        mode: 'cors',
        credentials: 'omit',
        headers: {
          ...this.getHeaders(),
          ...options.headers,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return await this.handleResponse<T>(response);
    } catch (error: unknown) {
      clearTimeout(timeoutId);

      // Check if it's a rate limit error and we should retry
      if (error instanceof ApiClientError && error.status === 429 && 
          retryCount < API_CONFIG.RETRY_ATTEMPTS) {
        const delay = error.retryAfter || API_CONFIG.RETRY_DELAY * Math.pow(2, retryCount);
        await this.sleep(delay);
        return this.fetchWithRetry<T>(url, options, retryCount + 1);
      }

      // For network errors, retry with exponential backoff
      if (retryCount < API_CONFIG.RETRY_ATTEMPTS && 
          (error instanceof TypeError || (error instanceof Error && error.name === 'AbortError'))) {
        const delay = API_CONFIG.RETRY_DELAY * Math.pow(2, retryCount);
        await this.sleep(delay);
        return this.fetchWithRetry<T>(url, options, retryCount + 1);
      }

      throw error;
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    return this.fetchWithRetry<T>(url, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    return this.fetchWithRetry<T>(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    return this.fetchWithRetry<T>(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    return this.fetchWithRetry<T>(url, { method: 'DELETE' });
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

// Helper function to check if error is an API error
export function isApiError(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError;
}

// Helper function to get error details
export function getErrorDetails(error: unknown) {
  if (isApiError(error)) {
    return {
      message: error.message,
      status: error.status,
      code: error.errorCode,
      details: error.details,
      requestId: error.requestId,
      rateLimit: error.rateLimit,
      retryAfter: error.retryAfter,
    };
  }
  
  return {
    message: error instanceof Error ? error.message : 'Unknown error',
    status: 0,
  };
}
